import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged, User } from 'firebase/auth';
import { getFirestore, enableNetwork, disableNetwork } from 'firebase/firestore';
import { getDatabase, ref, onValue, set, onDisconnect, serverTimestamp } from 'firebase/database';

// Firebase configuration - Replace with your actual config
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Display helpful setup messages
console.log('🔥 Firebase Initialized Successfully!');
console.log('📋 Project:', firebaseConfig.projectId);
console.log('🌐 Auth Domain:', firebaseConfig.authDomain);
console.log('');
console.log('🚨 FIRST TIME SETUP:');
console.log('1. Go to: https://console.firebase.google.com/project/gc-pathological-center/authentication/users');
console.log('2. Click "Add user"');
console.log('3. Email: admin@gc-pathological.com');
console.log('4. Password: admin123');
console.log('5. Login with these credentials');
console.log('');

// Initialize services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const realtimeDb = getDatabase(app);

// Enable offline persistence for Firestore
try {
  // This will enable offline persistence
  console.log('Firebase offline persistence enabled');
} catch (error) {
  console.warn('Firebase offline persistence failed:', error);
}

// Authentication helpers
export const loginAdmin = async (email: string, password: string): Promise<User> => {
  try {
    console.log('Attempting login with email:', email);
    console.log('Firebase auth instance:', auth);

    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    console.log('Login successful:', userCredential.user);
    return userCredential.user;
  } catch (error: unknown) { // Firebase auth errors have specific structure
    console.error('Login error:', error);
    const errorCode = (error as { code?: string }).code;
    const errorMessageText = (error as { message?: string }).message;

    console.error('Error code:', errorCode);
    console.error('Error message:', errorMessageText);

    // Provide more specific error messages
    let errorMessage = 'Login failed';
    switch (errorCode) {
      case 'auth/invalid-credential':
        errorMessage = '🚨 USER DOES NOT EXIST: You need to create this user in Firebase Authentication first. Click "Create User in Firebase" button below.';
        break;
      case 'auth/user-not-found':
        errorMessage = '👤 No user found with this email address. Create the user in Firebase Authentication first.';
        break;
      case 'auth/wrong-password':
        errorMessage = '🔐 Incorrect password. Check your password or recreate the user in Firebase.';
        break;
      case 'auth/invalid-email':
        errorMessage = '📧 Invalid email address format';
        break;
      case 'auth/user-disabled':
        errorMessage = '🚫 This account has been disabled';
        break;
      case 'auth/too-many-requests':
        errorMessage = '⏱️ Too many failed attempts. Please try again later';
        break;
      case 'auth/network-request-failed':
        errorMessage = '🌐 Network error. Please check your internet connection';
        break;
      case 'auth/configuration-not-found':
        errorMessage = '⚙️ Firebase configuration error. Check your project setup.';
        break;
      default:
        errorMessage = `❌ ${(error as Error).message || 'Login failed'}`;
    }

    throw new Error(errorMessage);
  }
};

export const registerUser = async (email: string, password: string): Promise<User> => {
  try {
    console.log('Attempting registration with email:', email);
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    console.log('Registration successful:', userCredential.user);
    return userCredential.user;
  } catch (error: unknown) {
    console.error('Registration error:', error);
    const errorCode = (error as { code?: string }).code;

    let errorMessage = 'Registration failed';
    switch (errorCode) {
      case 'auth/email-already-in-use':
        errorMessage = '📧 Email already in use. Please login instead.';
        break;
      case 'auth/invalid-email':
        errorMessage = '📧 Invalid email address format';
        break;
      case 'auth/weak-password':
        errorMessage = '🔐 Password is too weak. It should be at least 6 characters.';
        break;
      default:
        errorMessage = `❌ ${(error as Error).message || 'Registration failed'}`;
    }
    throw new Error(errorMessage);
  }
};

export const logoutAdmin = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Logout failed';
    throw new Error(errorMessage);
  }
};

export const onAuthChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Online presence system
export interface OnlineUser {
  uid: string;
  email: string;
  displayName: string;
  lastSeen: object | Date | number; // Firebase serverTimestamp returns an opaque object
  status: 'online' | 'offline';
  device: string;
  emoji: string;
}

const getRandomUserEmoji = (): string => {
  const emojis = ['👩‍⚕️', '👨‍⚕️', '🧑‍⚕️', '👩‍🔬', '👨‍🔬', '🧑‍🔬', '👩‍💻', '👨‍💻', '🧑‍💻'];
  return emojis[Math.floor(Math.random() * emojis.length)];
};

const getDeviceType = (): string => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (/mobile|android|iphone/.test(userAgent)) return 'Mobile';
  if (/tablet|ipad/.test(userAgent)) return 'Tablet';
  if (/mac/.test(userAgent)) return 'Mac';
  return 'Desktop';
};

export const setupUserPresence = (user: User): void => {
  const userStatusRef = ref(realtimeDb, `users/${user.uid}`);
  const connectedRef = ref(realtimeDb, '.info/connected');

  const userInfo: OnlineUser = {
    uid: user.uid,
    email: user.email || 'Unknown',
    displayName: user.displayName || user.email?.split('@')[0] || 'User',
    lastSeen: serverTimestamp(),
    status: 'online',
    device: getDeviceType(),
    emoji: getRandomUserEmoji()
  };

  onValue(connectedRef, (snapshot) => {
    if (snapshot.val() === true) {
      // Set user as online
      set(userStatusRef, userInfo);

      // Set user as offline when they disconnect
      onDisconnect(userStatusRef).set({
        ...userInfo,
        status: 'offline',
        lastSeen: serverTimestamp()
      });
    }
  });
};

export const getOnlineUsers = (callback: (users: OnlineUser[]) => void) => {
  const usersRef = ref(realtimeDb, 'users');
  return onValue(usersRef, (snapshot) => {
    const users: OnlineUser[] = [];
    snapshot.forEach((childSnapshot) => {
      const user = childSnapshot.val() as OnlineUser;
      if (user.status === 'online') {
        users.push(user);
      }
    });
    callback(users);
  });
};

// Network status helpers
export const enableOfflineMode = async (): Promise<void> => {
  await disableNetwork(db);
};

export const enableOnlineMode = async (): Promise<void> => {
  await enableNetwork(db);
};

// Firebase connectivity test
export const testFirebaseConnection = async (): Promise<{
  connected: boolean,
  projectId: string,
  authDomain: string,
  errors: string[]
}> => {
  const errors: string[] = [];

  try {
    console.log('Testing Firebase connection...');

    // Check if app is initialized
    if (!app) {
      errors.push('Firebase app not initialized');
    }

    // Check configuration
    const config = app.options;
    if (!config.projectId) errors.push('Missing project ID');
    if (!config.authDomain) errors.push('Missing auth domain');
    if (!config.apiKey) errors.push('Missing API key');

    // Check auth instance
    if (!auth) {
      errors.push('Firebase Auth not initialized');
    }

    console.log('Firebase connection test results:', {
      connected: errors.length === 0,
      projectId: config.projectId || 'NOT_SET',
      authDomain: config.authDomain || 'NOT_SET',
      errors
    });

    return {
      connected: errors.length === 0,
      projectId: config.projectId || 'NOT_SET',
      authDomain: config.authDomain || 'NOT_SET',
      errors
    };
  } catch (error: unknown) {
    console.error('Firebase connection test failed:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    errors.push(errorMessage);
    return {
      connected: false,
      projectId: 'ERROR',
      authDomain: 'ERROR',
      errors
    };
  }
};

export default app;